import { ErrorState, LoadingState, StaleBanner } from '../../components/ui/AsyncState';
import { useEffect, useState, type FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useRecoveryQuery as useQuery } from '../../components/ui/useRecoveryQuery';
import {
  getMarginConfig,
  updateMarginConfig,
} from '../../api/endpoints/admin';
import { useMutationHook } from '../../hooks/useMutationHook';
import { MatchingPoliciesTab } from './MatchingPoliciesTab';
import { OperationsTab } from './OperationsTab';
import { OpsTeamTab } from './OpsTeamTab';
import { NotificationPoliciesTab } from './NotificationPoliciesTab';
import { TruckTypesTab } from './TruckTypesTab';
import styles from './SettingsView.module.css';
import { staticQueryPolicy } from '../../api/queryPolicies';
import { PaymentsSettingsTab } from './PaymentsSettingsTab';
import { ApprovalLimitsTab } from './ApprovalLimitsTab';
import { AppUpdatesTab } from './AppUpdatesTab';
import { isDemo } from '../../demo/flag';

export type SettingsTab = 'margins' | 'payments' | 'approvals' | 'matching' | 'notification' | 'trucks' | 'operations' | 'appUpdates' | 'team';

/**
 * Platform settings view:
 *  - Margins: carrier and shipper marketplace margin rates
 *  - Matching policies: multi-factor scoring weights & candidate ranking rules
 *  - Notification policies: automated wave sizing, intervals, and anti-fatigue cooldowns
 *  - Truck types: the truck catalog every picker loads (#249)
 *  - App updates: minimum / latest app build per app and platform (#338)
 *  - Ops team: support phone, default open-order cap, team status + cap overrides
 */
export function SettingsView({ initialTab }: { initialTab?: SettingsTab } = {}) {
  const { t } = useTranslation();
  // Approval limits is demo-only until the build issue (#297).
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    initialTab && (initialTab !== 'approvals' || isDemo()) ? initialTab : 'margins',
  );
  const [paymentsDirty, setPaymentsDirty] = useState(false);
  const selectTab = (tab: SettingsTab) => {
    if (tab === activeTab) return;
    if (activeTab === 'payments' && paymentsDirty && !window.confirm('Discard unsaved payment settings and leave?')) return;
    setPaymentsDirty(false);
    setActiveTab(tab);
  };

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'margin-config'],
    queryFn: getMarginConfig,
    enabled: activeTab === 'margins',
    ...staticQueryPolicy,
  });

  const [carrierPct, setCarrierPct] = useState('');
  const [shipperPct, setShipperPct] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setCarrierPct(String(data.carrierMarginPct));
      setShipperPct(String(data.shipperMarginPct));
    }
  }, [data]);

  const saveMut = useMutationHook(
    () =>
      updateMarginConfig({
        carrierMarginPct: Number(carrierPct),
        shipperMarginPct: Number(shipperPct),
      }),
    {
      successMessage:
        'Margin rates updated. Only newly posted shipments and new bids use the new rates.',
      invalidateKeys: [
        ['admin', 'margin-config'],
        ['admin', 'shipments'],
        ['admin', 'dashboard'],
      ],
    },
  );

  const validate = (): string | null => {
    for (const [label, value] of [
      ['Partner margin', carrierPct],
      ['Shipper margin', shipperPct],
    ] as const) {
      if (value.trim() === '' || Number.isNaN(Number(value)))
        return `${label} must be a number.`;
      const n = Number(value);
      if (n < 0 || n > 50) return `${label} must be between 0 and 50.`;
      if (Math.round(n * 100) !== n * 100)
        return `${label} supports at most 2 decimal places.`;
    }
    return null;
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    saveMut.mutate(undefined);
  };

  return (
    <div className={styles.page}>
      {/* Settings Navigation Tabs */}
      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === 'payments' ? styles.tabActive : ''}`}
          onClick={() => selectTab('payments')}
        >{t('settings.view.tabPayments')}</button>
        {isDemo() && (
          <button
            type="button"
            className={`${styles.tabBtn} ${activeTab === 'approvals' ? styles.tabActive : ''}`}
            onClick={() => selectTab('approvals')}
          >
            {t('settings.view.tabApprovals')}
          </button>
        )}
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === 'margins' ? styles.tabActive : ''}`}
          onClick={() => selectTab('margins')}
        >
          {t('settings.view.tabMargins')}
        </button>
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === 'matching' ? styles.tabActive : ''}`}
          onClick={() => selectTab('matching')}
        >
          {t('settings.view.tabMatching')}
        </button>
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === 'notification' ? styles.tabActive : ''}`}
          onClick={() => selectTab('notification')}
        >
          {t('settings.view.tabNotification')}
        </button>
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === 'trucks' ? styles.tabActive : ''}`}
          onClick={() => selectTab('trucks')}
        >
          {t('settings.view.tabTrucks')}
        </button>
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === 'operations' ? styles.tabActive : ''}`}
          onClick={() => selectTab('operations')}
        >
          {t('settings.view.tabOperations')}
        </button>
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === 'appUpdates' ? styles.tabActive : ''}`}
          onClick={() => selectTab('appUpdates')}
        >
          {t('settings.view.tabAppUpdates')}
        </button>
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === 'team' ? styles.tabActive : ''}`}
          onClick={() => selectTab('team')}
        >
          {t('settings.view.tabTeam')}
        </button>
      </div>

      {activeTab === 'margins' && (
        <>
          {isLoading && <LoadingState label={t('settings.view.loadingMargins')} />}
          {isError && (data ? <StaleBanner onRetry={() => refetch()} /> : <ErrorState title={t('settings.view.loadMarginsError')} onRetry={() => refetch()} />)}
          {data && (
            <>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>{t('settings.view.marginsTitle')}</h3>
              <p className={styles.cardHint}>
                <Trans i18nKey="settings.view.marginsHint" components={{ strong: <strong /> }} />
              </p>

              <form onSubmit={submit}>
                <div className={styles.row}>
                  <div>
                    <label className={styles.label} htmlFor="carrierPct">
                      {t('settings.view.carrierMarginLabel')}
                    </label>
                    <div className={styles.inputWrap}>
                      <input
                        id="carrierPct"
                        className={styles.input}
                        type="number"
                        min="0"
                        max="50"
                        step="0.01"
                        value={carrierPct}
                        onChange={(e) => setCarrierPct(e.target.value)}
                      />
                      <span className={styles.pctSuffix}>%</span>
                    </div>
                  </div>
                  <div>
                    <label className={styles.label} htmlFor="shipperPct">
                      {t('settings.view.shipperMarginLabel')}
                    </label>
                    <div className={styles.inputWrap}>
                      <input
                        id="shipperPct"
                        aria-describedby="shipperPctHint"
                        className={styles.input}
                        type="number"
                        min="0"
                        max="50"
                        step="0.01"
                        value={shipperPct}
                        onChange={(e) => setShipperPct(e.target.value)}
                      />
                      <span className={styles.pctSuffix}>%</span>
                    </div>
                    <p id="shipperPctHint" className={styles.cardHint}>
                      {t('settings.view.shipperMarginHint')}
                    </p>
                  </div>
                </div>

                {error && <div className={styles.error}>{error}</div>}

                <div className={styles.actions}>
                  <button type="submit" className={styles.submit} disabled={saveMut.isLoading}>
                    {saveMut.isLoading ? 'Saving…' : 'Save margins'}
                  </button>
                </div>
              </form>

              {data.updatedAt && (
                <div className={styles.meta}>
                  {t('settings.view.lastChanged', { date: new Date(data.updatedAt).toLocaleString('en-IN') })}
                  {data.updatedBy ? ` by ${data.updatedBy}` : ''}{' '}
                  {t('settings.view.auditLogNote')}
                </div>
              )}
            </div>
          </>
        )}
        </>
      )}

      {activeTab === 'matching' && <MatchingPoliciesTab />}
      {activeTab === 'payments' && <PaymentsSettingsTab onDirtyChange={setPaymentsDirty} />}
      {activeTab === 'approvals' && isDemo() && <ApprovalLimitsTab />}

      {activeTab === 'notification' && <NotificationPoliciesTab />}

      {activeTab === 'trucks' && <TruckTypesTab />}

      {activeTab === 'operations' && <OperationsTab />}

      {activeTab === 'appUpdates' && <AppUpdatesTab />}

      {activeTab === 'team' && <OpsTeamTab />}
    </div>
  );
}
